import { useEffect, useState, type RefObject } from 'react';

/**
 * Pay arming: the button is enabled only after the card has been visible,
 * continuously, for ARM_DELAY_MS. The checkout can be framed by any page
 * (`frame-ancestors *`, so no-code merchants can embed it), and this is what
 * keeps a page from flashing the frame under the cursor, or hiding it behind
 * something, for a click the customer meant for something else. Where the
 * browser supports IntersectionObserver v2, "visible" also means not occluded
 * and not faded or transformed.
 */

export const ARM_DELAY_MS = 600;
export const VISIBLE_RATIO = 0.9;

type EntryLike = Pick<IntersectionObserverEntry, 'isIntersecting' | 'intersectionRatio' | 'intersectionRect' | 'rootBounds'> & {
  boundingClientRect: { height: number; width: number };
  isVisible?: boolean;
};

/**
 * ≥90% of the card in view, or (for a card taller than a small overlay or
 * phone viewport) ≥90% of the viewport filled by it. Without the second rule
 * a tall card in a 320 px frame could never arm.
 */
export function isEntryVisible(entry: EntryLike): boolean {
  if (!entry.isIntersecting) return false;
  if (entry.isVisible === false) return false;
  if (entry.intersectionRatio >= VISIBLE_RATIO) return true;
  const root = entry.rootBounds;
  if (!root || root.height <= 0) return false;
  const coversHeight = entry.intersectionRect.height >= VISIBLE_RATIO * Math.min(root.height, entry.boundingClientRect.height);
  const coversWidth = entry.intersectionRect.width >= VISIBLE_RATIO * Math.min(root.width, entry.boundingClientRect.width);
  return coversHeight && coversWidth;
}

/** Arms after `delayMs` of uninterrupted visibility; any loss resets the clock. */
export function createArmingTimer(onChange: (armed: boolean) => void, delayMs = ARM_DELAY_MS) {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let armed = false;
  return {
    setVisible(visible: boolean) {
      if (visible) {
        if (!armed && !timer) {
          timer = setTimeout(() => {
            timer = null;
            armed = true;
            onChange(true);
          }, delayMs);
        }
        return;
      }
      if (timer) clearTimeout(timer);
      timer = null;
      if (armed) {
        armed = false;
        onChange(false);
      }
    },
    dispose() {
      if (timer) clearTimeout(timer);
      timer = null;
    },
  };
}

export function useArming(ref: RefObject<HTMLElement | null>, active: boolean): boolean {
  const [armed, setArmed] = useState(false);

  useEffect(() => {
    setArmed(false);
    const el = ref.current;
    if (!active || !el) return;

    const arming = createArmingTimer(setArmed);
    let intersecting = false;
    const update = () => arming.setVisible(intersecting && document.visibilityState === 'visible');
    document.addEventListener('visibilitychange', update);

    let observer: IntersectionObserver | null = null;
    if (typeof IntersectionObserver === 'function') {
      // trackVisibility/delay are the v2 options; browsers without v2 ignore them.
      const options = {
        threshold: [0, 0.25, 0.5, 0.75, VISIBLE_RATIO, 1],
        trackVisibility: true,
        delay: 100,
      } as IntersectionObserverInit;
      observer = new IntersectionObserver((entries) => {
        const entry = entries[entries.length - 1];
        intersecting = isEntryVisible(entry as EntryLike);
        update();
      }, options);
      observer.observe(el);
    } else {
      intersecting = true;
      update();
    }

    return () => {
      observer?.disconnect();
      document.removeEventListener('visibilitychange', update);
      arming.dispose();
    };
  }, [ref, active]);

  return armed;
}
